import { useCallback, useEffect, useRef, useState } from "react";
import {
  buildSignalingUrl,
  connectSignaling,
  fetchSignalingTicket,
} from "./signaling";
import {
  createPeerSession,
  loadIceServers,
  sampleMediaRates,
  DEFAULT_AUDIO_BITRATE_KBPS,
  DEFAULT_VIDEO_BITRATE_KBPS,
  AUDIO_BITRATE_OPTIONS_KBPS,
  VIDEO_BITRATE_OPTIONS_KBPS,
} from "./peerConnection";

const WS_BASE = process.env.REACT_APP_SIGNALING_WS_URL || "";
const EMPTY_RATES = {
  audio: { send: null, recv: null },
  video: { send: null, recv: null },
};

/** @typedef {'idle'|'connecting'|'waiting'|'in_call'|'left'|'error'} SessionStatus */

/**
 * Room session — auto-connects to signaling and auto-starts the call when paired
 * (impolite peer offers; polite peer answers).
 */
export default function useRoomSession({
  roomId,
  peerId,
  displayName,
  role,
  getIdToken,
  localVideoRef,
  remoteVideoRef,
  inviteTicket = null,
  autoConnect = true,
}) {
  const [wsOpen, setWsOpen] = useState(false);
  const [roster, setRoster] = useState({});
  const [remotePeerId, setRemotePeerId] = useState(null);
  const [cameraOn, setCameraOn] = useState(false);
  const [micOn, setMicOn] = useState(true);
  const [logLines, setLogLines] = useState([]);
  const [connecting, setConnecting] = useState(false);
  const [connectionState, setConnectionState] = useState("new");
  const [roomError, setRoomError] = useState("");
  const [audioKbps, setAudioKbps] = useState(DEFAULT_AUDIO_BITRATE_KBPS);
  const [videoKbps, setVideoKbps] = useState(DEFAULT_VIDEO_BITRATE_KBPS);
  const [mediaPolicySource, setMediaPolicySource] = useState("local");
  const [canEditMediaPolicy, setCanEditMediaPolicy] = useState(true);
  const [mediaRates, setMediaRates] = useState(EMPTY_RATES);
  /** @type {[SessionStatus, function]} */
  const [sessionStatus, setSessionStatus] = useState("idle");
  const [statusDetail, setStatusDetail] = useState("");

  const signalingRef = useRef(null);
  const peerRef = useRef(null);
  const remotePeerIdRef = useRef(null);
  const selfIdRef = useRef(peerId);
  const audioKbpsRef = useRef(DEFAULT_AUDIO_BITRATE_KBPS);
  const videoKbpsRef = useRef(DEFAULT_VIDEO_BITRATE_KBPS);
  const applyingRemotePolicyRef = useRef(false);
  const applyRemoteMediaPolicyRef = useRef(async () => {});
  const lastStatsRef = useRef({ sent: {}, recv: {} });
  const intentionalLeaveRef = useRef(false);
  const connectGenRef = useRef(0);
  const autoOfferInFlightRef = useRef(false);
  const userLeftRef = useRef(false);
  const autoAttemptedRef = useRef(false);
  const connectRef = useRef(null);

  useEffect(() => {
    selfIdRef.current = peerId;
  }, [peerId]);

  useEffect(() => {
    remotePeerIdRef.current = remotePeerId;
  }, [remotePeerId]);

  useEffect(() => {
    audioKbpsRef.current = audioKbps;
  }, [audioKbps]);

  useEffect(() => {
    videoKbpsRef.current = videoKbps;
  }, [videoKbps]);

  // Guests (invite ticket) comply with room policy; hosts/stage may publish overrides.
  useEffect(() => {
    setCanEditMediaPolicy(!inviteTicket);
  }, [inviteTicket]);

  const log = useCallback((...args) => {
    const line = args
      .map((a) => (typeof a === "string" ? a : JSON.stringify(a)))
      .join(" ");
    setLogLines((prev) => [...prev.slice(-400), line]);
    console.log(...args);
  }, []);

  const teardown = useCallback(
    ({ markLeft = false } = {}) => {
      connectGenRef.current += 1;
      autoOfferInFlightRef.current = false;
      try {
        signalingRef.current?.close();
      } catch {
        /* ignore */
      }
      signalingRef.current = null;
      try {
        peerRef.current?.close(localVideoRef.current, remoteVideoRef.current);
      } catch {
        /* ignore */
      }
      peerRef.current = null;
      setWsOpen(false);
      setConnecting(false);
      setRoster({});
      setRemotePeerId(null);
      remotePeerIdRef.current = null;
      setCameraOn(false);
      setMicOn(true);
      setConnectionState("new");
      setMediaRates(EMPTY_RATES);
      lastStatsRef.current = { sent: {}, recv: {} };
      if (markLeft) {
        setSessionStatus("left");
        setStatusDetail("You left the room. Signaling is disconnected.");
      }
    },
    [localVideoRef, remoteVideoRef]
  );

  const ensurePeer = useCallback(() => {
    if (peerRef.current) return peerRef.current;
    const session = createPeerSession({
      role,
      iceServers: loadIceServers(),
      audioBitrateKbps: audioKbpsRef.current,
      videoBitrateKbps: videoKbpsRef.current,
      getRemotePeerId: () => remotePeerIdRef.current,
      getSelfId: () => selfIdRef.current,
      isPolite: () => {
        const remote = remotePeerIdRef.current;
        if (!remote) return false;
        return selfIdRef.current < remote;
      },
      sendSignal: (to, signal) => signalingRef.current?.sendSignal(to, signal),
      onRemoteStream: (stream) => {
        if (remoteVideoRef.current) {
          remoteVideoRef.current.srcObject = stream;
        }
      },
      onConnectionState: (pcState) => {
        setConnectionState(pcState || "new");
        if (pcState === "connected") {
          setSessionStatus((s) => (s === "left" ? s : "in_call"));
          setStatusDetail("Call connected.");
        } else if (pcState === "failed") {
          setStatusDetail("Peer connection failed.");
        }
      },
      log,
    });
    peerRef.current = session;
    return session;
  }, [role, log, remoteVideoRef]);

  const autoStartCallIfNeeded = useCallback(
    async (other, selfId) => {
      if (!other || !signalingRef.current?.isOpen()) return;
      const polite = selfId < other;
      if (polite) {
        ensurePeer();
        setSessionStatus((s) => (s === "left" ? s : "waiting"));
        setStatusDetail("Paired — waiting for the other peer to start the call…");
        log(`Paired with ${other}. Polite peer — waiting for offer.`);
        return;
      }
      if (autoOfferInFlightRef.current) return;
      autoOfferInFlightRef.current = true;
      try {
        log(`Paired with ${other}. Auto-starting call (offerer).`);
        setStatusDetail("Starting call…");
        const peer = ensurePeer();
        await peer.startCall();
        setSessionStatus((s) => (s === "left" ? s : "in_call"));
      } catch (e) {
        log("Auto-start call failed:", e?.message || String(e));
        setStatusDetail(e?.message || "Failed to start call");
      } finally {
        autoOfferInFlightRef.current = false;
      }
    },
    [ensurePeer, log]
  );

  const maybePair = useCallback(
    (nextRoster, selfId) => {
      const ids = Object.keys(nextRoster).filter((id) => id !== selfId);
      const other = ids[0] || null;
      if (!other) {
        remotePeerIdRef.current = null;
        setRemotePeerId(null);
        setSessionStatus((s) => (s === "left" || s === "connecting" ? s : "waiting"));
        setStatusDetail("In room — waiting for another participant…");
        log("No other peers yet; waiting.");
        return null;
      }
      remotePeerIdRef.current = other;
      setRemotePeerId(other);
      void autoStartCallIfNeeded(other, selfId);
      return other;
    },
    [log, autoStartCallIfNeeded]
  );

  useEffect(() => {
    if (connectionState !== "connected" && connectionState !== "connecting") {
      setMediaRates(EMPTY_RATES);
      lastStatsRef.current = { sent: {}, recv: {} };
      return undefined;
    }
    const tick = async () => {
      const pc = peerRef.current?.pc;
      if (!pc || pc.connectionState === "closed") return;
      try {
        const report = await pc.getStats();
        const agg = sampleMediaRates(report, lastStatsRef.current);
        setMediaRates({
          audio: {
            send: agg.audio.send || null,
            recv: agg.audio.recv || null,
          },
          video: {
            send: agg.video.send || null,
            recv: agg.video.recv || null,
          },
        });
      } catch {
        /* ignore */
      }
    };
    tick();
    const id = setInterval(tick, 2000);
    return () => clearInterval(id);
  }, [connectionState]);

  const hangup = useCallback(() => {
    intentionalLeaveRef.current = true;
    userLeftRef.current = true;
    teardown({ markLeft: true });
    log("Hung up — left room and disconnected signaling.");
  }, [teardown, log]);

  const connect = useCallback(async () => {
    if (!roomId || !peerId) {
      log("Missing roomId or peerId");
      setSessionStatus("error");
      setStatusDetail("Missing room or peer identity.");
      return;
    }
    if (!WS_BASE) {
      log("REACT_APP_SIGNALING_WS_URL is not configured");
      setSessionStatus("error");
      setStatusDetail("REACT_APP_SIGNALING_WS_URL is not configured.");
      return;
    }

    intentionalLeaveRef.current = false;
    userLeftRef.current = false;
    teardown({ markLeft: false });

    const gen = connectGenRef.current;
    setConnecting(true);
    setSessionStatus("connecting");
    setStatusDetail("Connecting to signaling…");
    setRoomError("");
    setRoster({});
    setRemotePeerId(null);

    let ticket = (inviteTicket || "").trim() || null;
    let token;

    if (ticket) {
      log("Using invite ticket for guest join");
    } else {
      try {
        token = await getIdToken();
      } catch (e) {
        log("Failed to get Cognito ID token:", e?.message || String(e));
        setConnecting(false);
        setSessionStatus("error");
        setStatusDetail(e?.message || "Failed to get ID token");
        return;
      }
      if (!token) {
        log("No Cognito ID token available");
        setConnecting(false);
        setSessionStatus("error");
        setStatusDetail("No Cognito ID token available.");
        return;
      }

      try {
        const minted = await fetchSignalingTicket({
          wsBase: WS_BASE,
          idToken: token,
          roomId,
        });
        ticket = minted.ticket;
        log("Got signaling ticket (expiresIn=", minted.expiresIn, ")");
      } catch (e) {
        log(
          "Ticket mint failed; falling back to ID token in WS URL:",
          e?.message || String(e)
        );
        setStatusDetail(
          `Ticket mint failed (${e?.message || e}); trying ID token…`
        );
      }
    }

    if (gen !== connectGenRef.current) return;

    let url;
    try {
      url = buildSignalingUrl({
        wsBase: WS_BASE,
        roomId,
        peerId,
        ticket,
        token: ticket ? undefined : token,
      });
    } catch (e) {
      log(e.message);
      setConnecting(false);
      setSessionStatus("error");
      setStatusDetail(e.message);
      return;
    }

    log(
      "Connecting →",
      url.replace(/token=[^&]+/, "token=***").replace(/ticket=[^&]+/, "ticket=***")
    );

    const conn = connectSignaling({
      url,
      attendee: {
        peerId,
        displayName: displayName || peerId,
        role,
      },
      log,
      onOpen: () => {
        if (gen !== connectGenRef.current) return;
        setWsOpen(true);
        setConnecting(false);
        setSessionStatus("waiting");
        setStatusDetail("Joined signaling — waiting for another participant…");
      },
      onClose: () => {
        if (gen !== connectGenRef.current) return;
        setWsOpen(false);
        setConnecting(false);
        if (intentionalLeaveRef.current || userLeftRef.current) {
          setSessionStatus("left");
          setStatusDetail("You left the room. Signaling is disconnected.");
        } else {
          setSessionStatus((s) => (s === "left" ? s : "error"));
          setStatusDetail((d) =>
            d && d !== "Connecting to signaling…"
              ? d
              : "Signaling connection closed."
          );
        }
      },
      onError: () => {
        if (gen !== connectGenRef.current) return;
        setConnecting(false);
      },
      onMessage: async (msg) => {
        if (gen !== connectGenRef.current) return;
        if (msg.type === "pong") return;

        if (msg.type === "roster") {
          const sid = msg.selfId || selfIdRef.current;
          selfIdRef.current = sid;
          const next = {};
          (msg.attendees || []).forEach((a) => {
            next[a.peerId] = a;
          });
          setRoster(next);
          maybePair(next, sid);
        } else if (msg.type === "attendee-joined") {
          setRoster((prev) => {
            const next = { ...prev, [msg.attendee.peerId]: msg.attendee };
            maybePair(next, selfIdRef.current);
            return next;
          });
        } else if (msg.type === "attendee-left") {
          setRoster((prev) => {
            const next = { ...prev };
            delete next[msg.peerId];
            return next;
          });
          if (remotePeerIdRef.current === msg.peerId) {
            peerRef.current?.closePeerConnection(remoteVideoRef.current);
            peerRef.current = null;
            setRemotePeerId(null);
            remotePeerIdRef.current = null;
            setConnectionState("new");
            setMediaRates(EMPTY_RATES);
            lastStatsRef.current = { sent: {}, recv: {} };
            autoOfferInFlightRef.current = false;
            if (signalingRef.current?.isOpen()) {
              setSessionStatus("waiting");
              setStatusDetail("Other participant left — waiting in room…");
            }
            log("Remote left; stayed in room on signaling.");
          }
        } else if (msg.type === "attendee-updated") {
          setRoster((prev) => ({
            ...prev,
            [msg.attendee.peerId]: msg.attendee,
          }));
        } else if (msg.type === "peer-unavailable") {
          log("Peer unavailable");
        } else if (msg.type === "signal") {
          const peer = ensurePeer();
          await peer.handleSignal(msg.from, msg.signal);
        } else if (msg.type === "media-policy") {
          await applyRemoteMediaPolicyRef.current(msg);
        } else if (msg.type === "broadcast") {
          log("broadcast from", msg.from, JSON.stringify(msg.payload));
        } else if (msg.type === "room-role-error") {
          const who = msg.occupant
            ? ` (occupied by ${
                msg.occupant.displayName || msg.occupant.peerId
              })`
            : "";
          const text = `${msg.message || "Room role error"}${who}`;
          log(`ROOM ROLE ERROR: ${text}`);
          setRoomError(text);
          setSessionStatus("error");
          setStatusDetail(text);
        }
      },
    });

    signalingRef.current = conn;
  }, [
    roomId,
    peerId,
    displayName,
    role,
    getIdToken,
    teardown,
    log,
    maybePair,
    ensurePeer,
    remoteVideoRef,
    inviteTicket,
  ]);

  connectRef.current = connect;

  const rejoin = useCallback(() => {
    userLeftRef.current = false;
    intentionalLeaveRef.current = false;
    autoAttemptedRef.current = true;
    void connect();
  }, [connect]);

  // Reset auto-connect attempt when join identity changes
  useEffect(() => {
    autoAttemptedRef.current = false;
  }, [roomId, peerId, role, inviteTicket]);

  // Auto-connect once when ready (Hang up / errors require explicit Rejoin)
  useEffect(() => {
    if (!autoConnect) return undefined;
    if (!roomId || !peerId) return undefined;
    if (userLeftRef.current) return undefined;
    if (autoAttemptedRef.current) return undefined;
    if (signalingRef.current?.isOpen() || connecting) return undefined;

    autoAttemptedRef.current = true;
    const t = setTimeout(() => {
      if (userLeftRef.current) return;
      void connectRef.current?.();
    }, 50);

    return () => clearTimeout(t);
  }, [autoConnect, roomId, peerId, role, inviteTicket, connecting]);

  const toggleCamera = useCallback(async () => {
    if (!signalingRef.current?.isOpen()) {
      log("Not connected to signaling");
      return;
    }
    const peer = ensurePeer();
    await peer.toggleCamera(localVideoRef.current);
    setCameraOn(peer.isCameraOn());
  }, [ensurePeer, localVideoRef, log]);

  const toggleMic = useCallback(() => {
    const peer = ensurePeer();
    const next = !micOn;
    peer.setMicEnabled(next);
    setMicOn(next);
  }, [ensurePeer, micOn]);

  const setAudioBitrate = useCallback(
    async (kbps, { publish = true } = {}) => {
      const v = Number(kbps);
      if (!Number.isFinite(v) || v <= 0) return;
      setAudioKbps(v);
      audioKbpsRef.current = v;
      if (peerRef.current) {
        try {
          await peerRef.current.setAudioBitrate(v);
        } catch (e) {
          log("Failed to update audio bitrate:", e?.message || String(e));
        }
      } else {
        log(`Audio bitrate preference set to ${v} kbps (applies on next offer)`);
      }
      if (publish && canEditMediaPolicy && !applyingRemotePolicyRef.current) {
        signalingRef.current?.send?.({
          type: "media-policy-update",
          audioKbps: v,
          videoKbps: videoKbpsRef.current,
        });
        setMediaPolicySource("room");
      }
    },
    [log, canEditMediaPolicy]
  );

  const setVideoBitrate = useCallback(
    async (kbps, { publish = true } = {}) => {
      const v = Number(kbps);
      if (!Number.isFinite(v) || v <= 0) return;
      setVideoKbps(v);
      videoKbpsRef.current = v;
      if (peerRef.current) {
        try {
          await peerRef.current.setVideoBitrate(v);
        } catch (e) {
          log("Failed to update video bitrate:", e?.message || String(e));
        }
      } else {
        log(`Video bitrate preference set to ${v} kbps`);
      }
      if (publish && canEditMediaPolicy && !applyingRemotePolicyRef.current) {
        signalingRef.current?.send?.({
          type: "media-policy-update",
          audioKbps: audioKbpsRef.current,
          videoKbps: v,
        });
        setMediaPolicySource("room");
      }
    },
    [log, canEditMediaPolicy]
  );

  const applyRemoteMediaPolicy = useCallback(
    async (msg) => {
      applyingRemotePolicyRef.current = true;
      try {
        const audio = Number(msg.audioKbps);
        const video = Number(msg.videoKbps);
        if (Number.isFinite(audio) && audio > 0) {
          setAudioKbps(audio);
          audioKbpsRef.current = audio;
        }
        if (Number.isFinite(video) && video > 0) {
          setVideoKbps(video);
          videoKbpsRef.current = video;
        }
        if (msg.source) setMediaPolicySource(msg.source);
        if (peerRef.current) {
          await peerRef.current.setMediaBitrates({
            audioKbps: Number.isFinite(audio) && audio > 0 ? audio : undefined,
            videoKbps: Number.isFinite(video) && video > 0 ? video : undefined,
          });
        }
        log(
          `Media policy applied (${msg.source || "room"}): audio=${msg.audioKbps} video=${msg.videoKbps} kbps`
        );
      } catch (e) {
        log("Failed to apply media policy:", e?.message || String(e));
      } finally {
        applyingRemotePolicyRef.current = false;
      }
    },
    [log]
  );
  applyRemoteMediaPolicyRef.current = applyRemoteMediaPolicy;

  const startCall = useCallback(async () => {
    if (!signalingRef.current?.isOpen()) {
      log("WS not open");
      return;
    }
    let remote = remotePeerIdRef.current;
    if (!remote) {
      remote = maybePair(roster, selfIdRef.current);
      if (!remote) return;
    }
    const peer = ensurePeer();
    await peer.startCall();
    setSessionStatus("in_call");
  }, [ensurePeer, log, maybePair, roster]);

  useEffect(() => {
    const localEl = localVideoRef.current;
    const remoteEl = remoteVideoRef.current;
    return () => {
      intentionalLeaveRef.current = true;
      connectGenRef.current += 1;
      signalingRef.current?.close();
      peerRef.current?.close(localEl, remoteEl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const rosterText =
    Object.values(roster)
      .map(
        (p) =>
          `• ${p.displayName || p.peerId} (${p.peerId})${
            p.role ? ` [${p.role}]` : ""
          }`
      )
      .join("\n") || "(room is empty)";

  const peerActive = !!(peerRef.current && peerRef.current.isPeerActive());
  const inRoom = sessionStatus === "waiting" || sessionStatus === "in_call" || sessionStatus === "connecting";

  return {
    wsOpen,
    connecting,
    roster,
    rosterText,
    remotePeerId,
    cameraOn,
    micOn,
    logLines,
    connectionState,
    roomError,
    clearRoomError: () => setRoomError(""),
    audioKbps,
    videoKbps,
    mediaPolicySource,
    canEditMediaPolicy,
    audioBitrateOptions: AUDIO_BITRATE_OPTIONS_KBPS,
    videoBitrateOptions: VIDEO_BITRATE_OPTIONS_KBPS,
    setAudioBitrate,
    setVideoBitrate,
    mediaRates,
    sessionStatus,
    statusDetail,
    connect,
    rejoin,
    hangup,
    toggleCamera,
    toggleMic,
    startCall,
    canHangup: inRoom || wsOpen || cameraOn || peerActive,
    canMedia: wsOpen,
    inRoom,
  };
}
